import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const STORAGE_KEY = 'journey-theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';
const MODES = ['light', 'dark', 'system'];

const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
};

// Shared workstations (POS tills especially) hand the same browser to several
// people, so the preference is stored per user once we know who is signed in.
// The unscoped key stays as the device default and as the value the pre-paint
// script in public/index.html reads.
const scopedKey = (scope) => (scope ? `${STORAGE_KEY}:${scope}` : STORAGE_KEY);

const readStoredMode = (scope = null) => {
  try {
    const saved = localStorage.getItem(scopedKey(scope));
    if (MODES.includes(saved)) return saved;
    if (!scope) return 'system';
    // First sign-in for this user on this device: inherit what the device is
    // already showing rather than snapping the theme out from under them.
    const deviceDefault = localStorage.getItem(STORAGE_KEY);
    return MODES.includes(deviceDefault) ? deviceDefault : 'system';
  } catch {
    // Private browsing or storage disabled.
    return 'system';
  }
};

const systemPrefersDark = () => {
  try {
    return window.matchMedia(DARK_QUERY).matches;
  } catch {
    return false;
  }
};

const resolve = (mode) => (mode === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : mode);

export const ThemeProvider = ({ children }) => {
  // `mode` is what the user picked (light | dark | system); `theme` is what
  // that resolves to right now. Only `mode` is persisted, so a user on
  // "system" keeps following the OS instead of being frozen at whatever it
  // happened to be when they first loaded the app.
  const [scope, setScope] = useState(null);
  const [mode, setMode] = useState(() => readStoredMode(null));
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  const theme = mode === 'system' ? (systemDark ? 'dark' : 'light') : mode;

  // Follow the OS while on "system".
  useEffect(() => {
    let mql;
    try {
      mql = window.matchMedia(DARK_QUERY);
    } catch {
      return undefined;
    }
    const onChange = (event) => setSystemDark(event.matches);
    // Safari < 14 only has the deprecated addListener.
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  // Keep other tabs of the same app in step.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key !== scopedKey(scope)) return;
      setMode(MODES.includes(event.newValue) ? event.newValue : 'system');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [scope]);

  // Called by ThemeScopeBridge when the signed-in user changes. Tracked in a
  // ref as well as state so the comparison does not have to happen inside a
  // setState updater, which must stay pure (StrictMode runs it twice).
  const scopeRef = useRef(null);
  const scopeToUser = useCallback((userId) => {
    const next = userId ? String(userId) : null;
    if (scopeRef.current === next) return;
    scopeRef.current = next;
    setScope(next);
    setMode(readStoredMode(next));
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const isDark = theme === 'dark';
    root.classList.toggle('dark', isDark);
    root.setAttribute('data-theme', theme);

    // Mobile browser chrome / PWA title bar should match the surface behind it.
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', isDark ? '#020617' : '#0A2540');

    try {
      localStorage.setItem(scopedKey(scope), mode);
      // Mirror to the device default so the pre-paint script (which cannot
      // know who is signed in) resolves to the right theme on next load.
      if (scope) localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Nothing to persist to; the in-memory state still drives this session.
    }
  }, [theme, mode, scope]);

  const setTheme = useCallback((next) => {
    setMode(MODES.includes(next) ? next : 'light');
  }, []);

  const toggleTheme = useCallback(() => {
    // Toggling from "system" should do the obvious thing: flip away from
    // whatever is currently on screen.
    setMode((current) => (resolve(current) === 'dark' ? 'light' : 'dark'));
  }, []);

  const value = useMemo(() => ({
    theme,
    mode,
    setTheme,
    setMode: setTheme,
    isDark: theme === 'dark',
    isSystem: mode === 'system',
    toggleTheme,
    scopeToUser,
  }), [theme, mode, setTheme, toggleTheme, scopeToUser]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

/**
 * Bridges the signed-in user id into ThemeContext. ThemeProvider has to sit
 * above AuthProvider so the login screens are themed too, which means it
 * cannot read the user directly - render this anywhere inside AuthProvider.
 */
export const ThemeScopeBridge = ({ userId }) => {
  const { scopeToUser } = useTheme();
  useEffect(() => {
    scopeToUser(userId);
  }, [userId, scopeToUser]);
  return null;
};

export default ThemeContext;
